/**
 * Topbar — breadcrumb (group/page), ⌘K search → /search, VI/EN, theme toggle
 * (set data-theme + localStorage), notification center (badge tổng + dropdown 5 hàng đợi),
 * badge PRODUCTION. US-AE.F0-0000-S04.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, Moon, Sun, Bell, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { AdminQueueSummary } from "@goppy/contracts";
import { setLang } from "@/lib/i18n";
import { persistTheme, type Theme } from "@/lib/theme";
import { cn } from "@/lib/utils";
import { useQueueSummary } from "@/features/dashboard/use-dashboard.js";
import { resolveEnvBadge, readAppEnv, type EnvTone } from "@/lib/env-badge.js";
import { DbEnvToggle } from "@/features/db-env/DbEnvToggle.js";

/** Class tone theo design token portal (không hex thô) — mỗi tone: viền/nền, chấm, chữ. */
const ENV_TONE_CLASS: Record<EnvTone, { wrap: string; dot: string; text: string }> = {
  info: { wrap: "border-tone-info-bd bg-tone-info-bg", dot: "bg-tone-info-fg", text: "text-tone-info-fg" },
  warning: { wrap: "border-tone-warning-bd bg-tone-warning-bg", dot: "bg-tone-warning-fg", text: "text-tone-warning-fg" },
  danger: { wrap: "border-tone-danger-bd bg-tone-danger-bg", dot: "bg-tone-danger-fg", text: "text-tone-danger-fg" },
  neutral: { wrap: "border-tone-neutral-bd bg-tone-neutral-bg", dot: "bg-tone-neutral-fg", text: "text-tone-neutral-fg" },
};

function useTheme() {
  // Nguồn sự thật = data-theme trên <html> (đã được initTheme() set lúc boot từ localStorage/system).
  const [theme, setTheme] = useState<Theme>(
    () =>
      (typeof document !== "undefined"
        ? (document.documentElement.getAttribute("data-theme") as Theme | null)
        : null) ?? "light",
  );
  const toggle = () => {
    const next: Theme = theme === "light" ? "dark" : "light";
    persistTheme(next); // set data-theme + lưu localStorage
    setTheme(next);
  };
  return { theme, toggle };
}

const NOTIF = [
  { to: "/drift", labelKey: "n.drift", tone: "danger", k: "drift" },
  { to: "/disputes", labelKey: "n.disputes", tone: "danger", k: "disputes" },
  { to: "/risk", labelKey: "n.risk", tone: "warning", k: "risk" },
  { to: "/gdpr", labelKey: "n.gdpr", tone: "info", k: "gdpr" },
  { to: "/abuse", labelKey: "n.abuse", tone: "warning", k: "abuse" },
] as const satisfies readonly { to: string; labelKey: string; tone: string; k: keyof AdminQueueSummary }[];

export interface TopbarProps {
  crumbGroup: string;
  crumbPage: string;
}

export function Topbar({ crumbGroup, crumbPage }: TopbarProps) {
  const { t, i18n } = useTranslation();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const [notifOpen, setNotifOpen] = useState(false);
  // Badge đọc THẬT từ /admin/queues/summary (thay QUEUE_COUNTS hardcode). Chưa có → count 0.
  const { data: queues } = useQueueSummary();
  const countOf = (k: keyof AdminQueueSummary) => queues?.[k] ?? 0;
  const total = NOTIF.reduce((s, n) => s + countOf(n.k), 0);
  const lang = i18n.language;
  // Badge môi trường suy từ build env (S03 · @UC9136) — unset/lạ → LOCAL, KHÔNG hardcode PRODUCTION.
  const envBadge = resolveEnvBadge(readAppEnv());
  const envTone = ENV_TONE_CLASS[envBadge.tone];

  // ⌘K / Ctrl+K → mở global search (khớp hint hiển thị trên nút). Prototype dùng phím tắt này.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        nav("/search");
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [nav]);

  return (
    // 🔴 US-AI-0286 @UC9136 — spacing hẹp ở base + overflow-x-clip: ở 768px gap-3.5+px-[22px] đẩy body
    //   cuộn ngang (795>768). Giảm gap/px ở base, khôi phục ở md; clip phần thừa ⇒ body KHÔNG cuộn ngang.
    <header className="sticky top-0 z-20 flex h-14 shrink-0 items-center gap-2 overflow-x-clip border-b border-border bg-surface px-3 md:gap-3.5 md:px-[22px]">
      <nav aria-label={t("ui.breadcrumb")} className="flex min-w-0 items-center gap-2 text-[13px]">
        <span className="text-fg-subtle">{crumbGroup}</span>
        <span className="text-fg-subtle">/</span>
        <span className="truncate font-semibold text-fg">{crumbPage}</span>
      </nav>
      <div className="flex-1" />

      {/* US-AIF.P0-0247 (@UC9419): đổi môi trường dữ liệu (Thật/Sandbox) — chỉ SUPER_ADMIN/OPS thấy. */}
      <DbEnvToggle />

      {/* T04 (@UC9136): search thu về icon-only (giải phóng chỗ) + tooltip hover (title/aria-label)
          kèm shortcut. Click → /search; ⌘K/Ctrl+K vẫn hoạt động (useEffect trên). */}
      <button
        onClick={() => nav("/search")}
        title={t("ui.search_hint")}
        aria-label={t("ui.search_hint")}
        className="flex h-[34px] w-[34px] items-center justify-center rounded-sm border border-border bg-muted text-fg-muted"
      >
        <Search className="h-[18px] w-[18px]" aria-hidden />
      </button>

      <div className="flex h-[34px] items-center rounded-sm border border-border bg-muted p-0.5">
        {(["vi", "en"] as const).map((l) => (
          <button
            key={l}
            onClick={() => setLang(l)}
            aria-pressed={lang === l}
            className={cn(
              "h-7 min-w-[34px] rounded-[6px] px-2.5 text-[12px] font-semibold",
              lang === l ? "bg-surface text-fg shadow" : "text-fg-subtle",
            )}
          >
            {l.toUpperCase()}
          </button>
        ))}
      </div>

      <button
        onClick={toggle}
        aria-label={t("ui.toggle_theme")}
        className="flex h-[34px] w-[34px] items-center justify-center rounded-sm border border-border bg-muted text-fg-muted"
      >
        {theme === "light" ? <Moon className="h-[18px] w-[18px]" /> : <Sun className="h-[18px] w-[18px]" />}
      </button>

      <div className="relative">
        <button
          onClick={() => setNotifOpen((o) => !o)}
          aria-label={t("ui.needs_attention")}
          aria-expanded={notifOpen}
          className="relative flex h-[34px] w-[34px] items-center justify-center rounded-sm border border-border bg-muted text-fg-muted"
        >
          <Bell className="h-[18px] w-[18px]" />
          {total > 0 && (
            <span className="absolute -right-1.5 -top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full border-2 border-surface bg-tone-danger-fg px-1 text-[10.5px] font-bold text-white">
              {total}
            </span>
          )}
        </button>
        {notifOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
            <div className="absolute right-0 top-[calc(100%+8px)] z-50 w-[340px] overflow-hidden rounded-lg border border-border bg-surface shadow-lg">
              <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <span className="text-[14px] font-semibold">{t("ui.needs_attention")}</span>
                <span className="text-[12px] text-fg-subtle">
                  {total} {t("ui.items")}
                </span>
              </div>
              <div className="p-1.5">
                {NOTIF.map((n) => (
                  <button
                    key={n.to}
                    onClick={() => {
                      setNotifOpen(false);
                      nav(n.to);
                    }}
                    className="flex w-full items-center gap-3 rounded-md px-2.5 py-2.5 text-left hover:bg-muted"
                  >
                    <span
                      className={cn(
                        "flex h-[22px] min-w-[22px] items-center justify-center rounded-full px-1.5 text-[12px] font-bold",
                        n.tone === "danger"
                          ? "bg-tone-danger-bg text-tone-danger-fg"
                          : n.tone === "warning"
                            ? "bg-tone-warning-bg text-tone-warning-fg"
                            : "bg-tone-info-bg text-tone-info-fg",
                      )}
                    >
                      {countOf(n.k)}
                    </span>
                    <span className="flex-1 text-[13px] font-medium">{t(n.labelKey)}</span>
                    <ChevronRight className="h-4 w-4 text-fg-subtle" />
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      <div
        data-testid="env-badge"
        data-env-tone={envBadge.tone}
        className={cn("flex h-[34px] items-center gap-1.5 rounded-sm border px-2.5", envTone.wrap)}
      >
        <span className={cn("h-[7px] w-[7px] rounded-full", envTone.dot)} />
        <span className={cn("text-[12px] font-semibold tracking-[.02em]", envTone.text)}>
          {envBadge.label}
        </span>
      </div>
    </header>
  );
}
